/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, Button, CircularProgress, Typography, useMediaQuery } from "@mui/material";
import UpdateImageUpload from "../../components/inputs/uploadImageUpdate";
import CustomInput from "../../components/inputs/customInput";
import { fileApiRoute } from "../../services/networkService";
import StatusDropdown from "../../components/dropdowns/statusDropdown";
import BranchDropdown from "../../components/dropdowns/branchDropdown";
import DriveFileRenameOutlineOutlinedIcon from '@mui/icons-material/DriveFileRenameOutlineOutlined';
import { useTheme } from "@emotion/react";
import Popup from "../../components/popups/popup";
import { useNavigate, useParams } from "react-router-dom";
import DriverService from "../../services/driverService";
import CityDropdown from "../../components/dropdowns/cityDropdown";
import SupervisorDropdown from "../../components/dropdowns/supervisorDropdown";
import { v4 as uuidv4 } from 'uuid';
import StreetDriverListForm from "../../components/subFroms/streetDriverListForm";

const UpdateDriver = () => {
    const { t } = useTranslation();
    const theme = useTheme();
    const navigate = useNavigate();
    const isTablet = useMediaQuery(theme.breakpoints.down('md'));

    const { id } = useParams();

    const [driver, setDriver] = useState({});
    const [selectedCity, setSelectedCity] = useState(null);
    const [selectedBranch, setSelectedBranch] = useState(null);
    const [selectedSupervisor, setSelectedSupervisor] = useState(null);
    const [selectedStatus, setSelectedStatus] = useState('');
    const [selectedImage, setSelectedImage] = useState(null);
    const [selectedIDImage, setSelectedIDImage] = useState(null);

    const [formValues, setFormValues] = useState({
        name: driver.name,
        phone: driver.phone,
        phoneTwo: driver.phoneTwo,
        password: '',
        confirmPassword: '',
        note: driver.note,
        transportationPrice: driver.transportationPrice,
        cityId: driver.cityId,
        branchId: driver.branchId,
        supervisorId: driver.supervisorId,
        status: driver.status,
        deleteImage: false,
        deleteIDImage: false,
        prices: driver.streetDrivers
    });

    const [updateErrors, setUpdateErrors] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const [isModalLoading, setIsModalLoading] = useState(true);

    const [showPopup, setShowPopup] = useState(false);
    const [popupMsg, setPopupMsg] = useState(false);
    const [popupType, setPopupType] = useState(false);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormValues((prevValues) => ({
            ...prevValues,
            [name]: value,
        }));
    };

    const handleNumberChange = (e) => {
        const { name, value } = e.target;
        const numericValue = value.replace(/,/g, "");

        setFormValues((prevValues) => ({
            ...prevValues,
            [name]: numericValue,
        }));
    };


    const handleImageChange = (file) => {
        setSelectedImage(file);
    };

    const handleIDImageChange = (file) => {
        setSelectedIDImage(file);
    };

    const validateForm = () => {
        const newUpdateErrors = {};

        if (formValues.password && formValues.password.length < 8) {
            newUpdateErrors.password = t('passwordMin');
        }

        if (isNaN(formValues.transportationPrice)) {
            newUpdateErrors.transportationPrice = t('transportationPriceNumber');
        }

        if (formValues.password !== formValues.confirmPassword) {
            newUpdateErrors.confirmPassword = t('confirmPasswordMatch');
        }

        formValues.prices.forEach((price, index) => {
            if (!price.streetId) price.streetError = t('streetRequired');
            else price.streetError = "";
            if (!price.transportationPrice) price.transportationPriceError = t('transportationPriceRequired');
            else price.transportationPriceError = "";
            if (isNaN(price.transportationPrice)) price.transportationPriceError = t('transportationPriceNumber');
        });

        setUpdateErrors(newUpdateErrors);

        const checkError = formValues.prices.some((price) => (price.streetError !== "" || price.transportationPriceError !== ""));

        return Object.keys(newUpdateErrors).length === 0 && !checkError;
    };


    const handleSubmit = async (e) => {
        e.preventDefault();

        if (isLoading) return;

        if (validateForm()) {
            try {
                setIsLoading(true);
                const formData = new FormData();

                const { prices, ...formInfo } = formValues;

                for (const key in formInfo) {
                    if (formInfo[key] && formInfo[key] !== '')
                        formData.append(key, formInfo[key]);
                }

                if (selectedImage) {
                    formData.append('image', selectedImage);
                }

                if (selectedIDImage) {
                    formData.append('IDImage', selectedIDImage);
                }

                prices.forEach((price, index) => {
                    formData.append(`streets[${index}][streetId]`, price.streetId);
                    formData.append(`streets[${index}][transportationPrice]`, price.transportationPrice);
                });

                const data = await DriverService.update(id, formData);
                if (data.status === 200) {
                    setPopupMsg(data.data.message);
                    setPopupType('success');
                    setShowPopup(true);
                    navigate('/drivers');
                }

                setUpdateErrors({});
            } catch (error) {
                if (error.response?.status === 400) {
                    const responseErrors = error.response.data || {};
                    const formattedErrors = {};

                    Object.keys(responseErrors).forEach((field) => {
                        formattedErrors[field] = responseErrors[field];
                    });

                    setUpdateErrors(formattedErrors);
                } else {
                    setPopupMsg(error.response.data.error);
                    setPopupType('error');
                    console.error("An unexpected error occurred:", error);
                    setUpdateErrors({ general: error.response.data.error });
                }
            } finally {
                setIsLoading(false);
            }
        }
    };

    const fetchDriver = async () => {
        try {
            const data = await DriverService.fetchOne(id);
            setDriver(data);
            setIsModalLoading(false);
        } catch (error) {
            console.error('Error fetching driver:', error);
        }
    };

    useEffect(() => {
        if (driver) {
            setFormValues({
                name: driver.name || '',
                phone: driver.phone || '',
                phoneTwo: driver.phoneTwo || '',
                note: driver.note || '',
                cityId: driver.cityId || '',
                branchId: driver.branchId || '',
                supervisorId: driver.supervisorId || '',
                transportationPrice: driver.transportationPrice || 0,
                prices: (driver.streetDrivers || []).map((street) => ({
                    id: uuidv4(),
                    streetId: street.street.id,
                    name: street.street.name,
                    transportationPrice: street.transportationPrice || "",
                    streetError: "",
                    transportationPriceError: "",
                })),
            });
            setSelectedCity(driver.city || null);
            setSelectedBranch(driver.branch || null);
            setSelectedSupervisor(driver.supervisor || null);
            setSelectedStatus(t(driver.status) || '');
            setUpdateErrors({});
        }
    }, [driver]);


    useEffect(() => {
        setIsModalLoading(true);
        fetchDriver();
    }, []);



    return (
        <Box sx={{
            p: isTablet ? 1 : 2,
            backgroundColor: theme.palette.secondary.main,
        }}>
            {showPopup && (
                <Popup
                    text={popupMsg}
                    type={popupType}
                    onClose={() => setShowPopup(false)}
                />
            )}

            <Box
                component="form"
                onSubmit={handleSubmit}
                sx={{
                    width: '100%',
                    bgcolor: 'background.paper',
                    borderRadius: 2,
                    border: 1,
                    borderColor: theme.palette.customGrey.borderInput,
                    p: isTablet ? 1 : 4
                }}
            >
                {
                    isModalLoading ? <Box sx={{ justifyContent: 'center', display: 'flex', my: 10 }} >
                        <CircularProgress color="primary" size={28} />
                    </Box>
                        : <Box>
                            <Box sx={{
                                display: 'flex',
                                justifyContent: 'space-between'
                            }}>
                                <Typography id="transition-modal-title" variant="h6" component="h2">
                                    {t('updateDriver')}
                                </Typography>
                            </Box>

                            {/* row 1 */}
                            <Box sx={{
                                display: isTablet ? 'unset' : 'flex',
                                justifyContent: 'space-between'
                            }}>
                                {/* city */}
                                <Box width={isTablet ? '100%' : '32%'}>
                                    <CityDropdown
                                        helperText={updateErrors.city}
                                        error={!!updateErrors.city}
                                        setFormValues={setFormValues}
                                        isFilter={false}
                                        selectedCity={selectedCity}
                                        setSelectedCity={setSelectedCity}
                                        disableSuperAdmin={true}
                                    />
                                </Box>

                                {/* Branch */}
                                <Box width={isTablet ? '100%' : '32%'}>
                                    <BranchDropdown
                                        helperText={updateErrors.branch}
                                        error={!!updateErrors.branch}
                                        setFormValues={setFormValues}
                                        isFilter={false}
                                        selectedBranch={selectedBranch}
                                        setSelectedBranch={setSelectedBranch}
                                        cityId={selectedCity && selectedCity.id}
                                    />
                                </Box>


                                {/* Branch */}
                                <Box width={isTablet ? '100%' : '32%'}>
                                    <SupervisorDropdown
                                        helperText={updateErrors.supervisor}
                                        error={!!updateErrors.supervisor}
                                        setFormValues={setFormValues}
                                        isFilter={false}
                                        selectedSupervisor={selectedSupervisor}
                                        setSelectedSupervisor={setSelectedSupervisor}
                                    />
                                </Box>
                            </Box>


                            {/* row 2 */}
                            <Box sx={{
                                display: isTablet ? 'unset' : 'flex',
                                justifyContent: 'space-between'
                            }}>
                                {/* Status */}
                                <Box width={isTablet ? '100%' : '32%'}>
                                    <StatusDropdown
                                        helperText={updateErrors.status}
                                        error={!!updateErrors.status}
                                        setFormValues={setFormValues}
                                        isFilter={false}
                                        selectedStatus={selectedStatus}
                                        setSelectedStatus={setSelectedStatus}
                                    />
                                </Box>

                                <Box width={isTablet ? '100%' : '32%'}>
                                    <CustomInput
                                        label={t('name')}
                                        placeholder={t('nameHere')}
                                        name="name"
                                        type='text'
                                        value={formValues.name}
                                        error={!!updateErrors.name}
                                        helperText={updateErrors.name}
                                        onChange={handleChange}
                                    />
                                </Box>

                                <Box width={isTablet ? '100%' : '32%'}>
                                    <CustomInput
                                        label={t('transportationPrice')}
                                        placeholder={t('transportationPriceHere')}
                                        name="transportationPrice"
                                        type='text'
                                        value={formValues.transportationPrice ? Number(formValues.transportationPrice).toLocaleString() : 0}
                                        error={!!updateErrors.transportationPrice}
                                        helperText={updateErrors.transportationPrice}
                                        onChange={handleNumberChange}
                                    />
                                </Box>

                            </Box>


                            {/* row 3 */}
                            <Box sx={{
                                display: isTablet ? 'unset' : 'flex',
                                justifyContent: 'space-between'
                            }}>
                                <Box width={isTablet ? '100%' : '32%'}>
                                    <CustomInput
                                        label={t('phone')}
                                        placeholder={t('phoneHere')}
                                        name="phone"
                                        type='text'
                                        value={formValues.phone}
                                        error={!!updateErrors.phone}
                                        helperText={updateErrors.phone}
                                        onChange={handleChange}
                                    />
                                </Box>

                                <Box width={isTablet ? '100%' : '32%'}>
                                    <CustomInput
                                        label={t('phoneTwo')}
                                        placeholder={t('phoneTwoHere')}
                                        name="phoneTwo"
                                        type='text'
                                        value={formValues.phoneTwo}
                                        onChange={handleChange}
                                    /></Box>

                                <Box width={isTablet ? '100%' : '32%'}>
                                    <CustomInput
                                        label={t('note')}
                                        placeholder={t('noteHere')}
                                        name="note"
                                        type='text'
                                        value={formValues.note}
                                        onChange={handleChange}
                                    />
                                </Box>
                            </Box>


                            {/* row 4 */}
                            <Box sx={{
                                display: isTablet ? 'unset' : 'flex',
                                justifyContent: 'space-between'
                            }}>
                                <Box width={isTablet ? '100%' : '32%'}>
                                    <CustomInput
                                        label={t('password')}
                                        placeholder="******"
                                        name="password"
                                        type="password"
                                        value={formValues.password}
                                        onChange={handleChange}
                                        error={!!updateErrors.password}
                                        helperText={updateErrors.password}
                                    /></Box>

                                <Box width={isTablet ? '100%' : '32%'}>
                                    <CustomInput
                                        label={t('confirmPassword')}
                                        placeholder="******"
                                        name="confirmPassword"
                                        type="password"
                                        value={formValues.confirmPassword}
                                        onChange={handleChange}
                                        error={!!updateErrors.confirmPassword}
                                        helperText={updateErrors.confirmPassword}
                                    /></Box>


                                <Box width={isTablet ? '100%' : '32%'}>
                                </Box>

                            </Box>


                            {/* images */}
                            <Box
                                sx={{
                                    display: isTablet ? 'unset' : 'flex',
                                    justifyContent: 'space-around',
                                    width: '100%'
                                }}>
                                <Box sx={{ width: isTablet ? '20vw' : '30vw' }}>
                                    <UpdateImageUpload
                                        label={t('uploadImage')}
                                        name={t('image')}
                                        value={'deleteImage'}
                                        onChange={handleImageChange}
                                        image={`${fileApiRoute}/${driver.image}`}
                                        setFormValues={setFormValues}
                                    />
                                </Box>

                                <Box sx={{ width: isTablet ? '20vw' : '30vw' }}>
                                    <UpdateImageUpload
                                        label={t('uploadIDImage')}
                                        name={t('IDimage')}
                                        value={'deleteIDImage'}
                                        onChange={handleIDImageChange}
                                        image={`${fileApiRoute}/${driver.IDImage}`}
                                        setFormValues={setFormValues}
                                    />
                                </Box>

                            </Box>

                            {/* row 6 prices */}
                            <StreetDriverListForm
                                formValues={formValues}
                                setFormValues={setFormValues}
                                cityId={selectedCity ? selectedCity.id : driver.cityId}
                            />

                            {updateErrors.general && (
                                <Typography align="center" color="error" variant="body2" sx={{ mt: 2 }}>
                                    {updateErrors.general}
                                </Typography>
                            )}

                            <Box sx={{
                                display: 'flex', justifyContent: 'center', alignItems: 'center',
                                mt: updateErrors.general ? 2 : 4
                            }}>
                                <Button
                                    type="submit"
                                    variant="contained"
                                    color="primary"
                                    sx={{ textTransform: 'none', width: 140, height: 40 }}
                                >
                                    {isLoading ? <CircularProgress color="secondary" size={28} /> :
                                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                            <p>{t('update')}</p>
                                            <DriveFileRenameOutlineOutlinedIcon fontSize="small" sx={{ mx: 0.4 }} />
                                        </Box>
                                    }
                                </Button>
                            </Box>
                        </Box>
                }

            </Box>
        </Box>
    );
}

export default UpdateDriver;